const MOBILE_QUERY = '(hover: none) and (pointer: coarse)';
const PORTRAIT_QUERY = '(orientation: portrait)';
const ROTATE_CLASS = 'rotate-to-landscape';
const ROTATABLE_PAGES = new Set(['play', 'single']);

let mobileMql: MediaQueryList | null = null;
let portraitMql: MediaQueryList | null = null;
let userOptedInCssRotate = false;
let rotatePromptEl: HTMLDivElement | null = null;

function isRotatablePage(): boolean {
  const page = document.body.dataset.page ?? '';
  return ROTATABLE_PAGES.has(page);
}

function shouldRotate(): boolean {
  if (!mobileMql || !portraitMql) return false;
  if (!isRotatablePage()) return false;
  if (!mobileMql.matches || !portraitMql.matches) return false;
  return userOptedInCssRotate;
}

function applyRotation(): void {
  document.body.classList.toggle(ROTATE_CLASS, shouldRotate());
  syncPrompt();
}

function syncPrompt(): void {
  if (typeof document === 'undefined') return;
  const shouldShow =
    !!mobileMql?.matches && !!portraitMql?.matches && isRotatablePage() && !userOptedInCssRotate;
  if (shouldShow) showPrompt();
  else hidePrompt();
}

function showPrompt(): void {
  if (rotatePromptEl) return;
  const overlay = document.createElement('div');
  overlay.id = '__rotate_prompt';
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:9998;background:rgba(0,0,0,0.72);display:flex;align-items:center;justify-content:center;padding:24px;';
  const card = document.createElement('div');
  card.style.cssText =
    'background:rgb(var(--wood-paper));color:rgb(var(--wood-ink));border-radius:16px;padding:24px 20px;max-width:320px;width:100%;text-align:center;box-shadow:0 8px 24px rgba(0,0,0,0.3);';
  const icon = document.createElement('div');
  icon.style.cssText = 'font-size:42px;margin-bottom:12px;';
  icon.textContent = '↻';
  const title = document.createElement('div');
  title.style.cssText = 'font-size:17px;font-weight:700;margin-bottom:8px;';
  title.textContent = '기기를 가로로 돌려주세요';
  const desc = document.createElement('div');
  desc.style.cssText =
    'font-size:13px;line-height:18px;color:rgb(var(--wood-bark));margin-bottom:20px;';
  desc.textContent =
    '가로 화면에서 더 편하게 플레이할 수 있어요. 자동회전이 꺼져 있다면 아래 버튼을 눌러주세요.';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = '이대로 사용';
  btn.style.cssText =
    'background:rgb(var(--card-navy));color:rgb(var(--wood-paper));border:0;border-radius:10px;padding:12px 18px;font-size:14px;font-weight:600;width:100%;cursor:pointer;';
  btn.addEventListener('click', () => {
    userOptedInCssRotate = true;
    applyRotation();
  });
  card.append(icon, title, desc, btn);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  rotatePromptEl = overlay;
}

function hidePrompt(): void {
  if (!rotatePromptEl) return;
  rotatePromptEl.remove();
  rotatePromptEl = null;
}

export function initOrientationLock(): void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  mobileMql = window.matchMedia(MOBILE_QUERY);
  portraitMql = window.matchMedia(PORTRAIT_QUERY);
  applyRotation();
  mobileMql.addEventListener('change', applyRotation);
  portraitMql.addEventListener('change', applyRotation);
}

/**
 * 라우트 전환 직후 호출. 현재 body[data-page]에 맞춰 회전 클래스를 재평가.
 * 비게임 페이지로 이동 시 회전 해제 및 opt-in 플래그 리셋.
 */
export function syncOrientationToPage(): void {
  if (!mobileMql || !portraitMql) return;
  if (!isRotatablePage()) {
    userOptedInCssRotate = false;
  }
  applyRotation();
}

export function isRotationActive(): boolean {
  if (typeof document === 'undefined') return false;
  return document.body.classList.contains(ROTATE_CLASS);
}

/**
 * 회전 활성 시 시각 좌표(viewport 좌표)를 회전 전 논리 좌표로 변환.
 * body가 `rotate(90deg) translateY(-100vw)` + origin top-left이면
 * 시각 (clientX, clientY) ↔ 논리 (clientY, visualWidth - clientX).
 * 회전 비활성 시 입력 그대로 반환.
 */
export function toLogicalPoint(clientX: number, clientY: number): { x: number; y: number } {
  if (!isRotationActive()) return { x: clientX, y: clientY };
  const visualWidth = window.innerWidth;
  return { x: clientY, y: visualWidth - clientX };
}

/**
 * standalone 모드(PWA 설치 후 홈 화면에서 실행) 여부.
 * Android/Chrome: matchMedia, iOS Safari: navigator.standalone.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/**
 * 사용자 제스처 시점에 풀스크린 진입 + landscape orientation lock 시도.
 * Android Chrome에선 진짜 가로 잠금됨, iOS Safari에선 둘 다 무시되지만
 * 호출 자체는 안전 (모든 reject는 swallow). 데스크톱이거나 standalone이면 skip.
 */
export async function requestImmersiveLandscape(): Promise<void> {
  if (typeof window === 'undefined') return;
  const isMobile = window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ?? false;
  if (!isMobile) return;
  const root = document.documentElement;
  try {
    if (root.requestFullscreen && !document.fullscreenElement) {
      await root.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
    }
  } catch {
    // ignore
  }
  type OrientationLock = ScreenOrientation & {
    lock?: (o: 'landscape' | 'landscape-primary') => Promise<void>;
  };
  const orientation = screen.orientation as OrientationLock | undefined;
  try {
    await orientation?.lock?.('landscape').catch(() => undefined);
  } catch {
    // ignore
  }
}

/**
 * 가로 잠금 해제. 비게임 페이지로 돌아갈 때 호출하여
 * 사용자가 기기를 세로로 들었을 때 자연스럽게 세로 레이아웃이 되도록 함.
 * iOS Safari·데스크톱에서는 no-op.
 */
export function releaseOrientationLock(): void {
  if (typeof window === 'undefined') return;
  const orientation = screen.orientation as ScreenOrientation & { unlock?: () => void };
  try {
    orientation?.unlock?.();
  } catch {
    // ignore
  }
}
